import { useEffect, useState } from 'react';

/**
 * Custom hook that delays showing the loading state until a specified delay has passed.
 * This prevents loading flicker for fast API responses that complete quickly.
 *
 * @param loading - The actual loading state from the API/query
 * @param delay - The minimum delay in milliseconds before showing loading state (default: 200ms)
 * @returns The delayed loading boolean - only returns true if loading has been true for longer than the delay
 *
 * @example
 * const { isLoading } = useQuery(...);
 * const delayedLoading = useDelayedLoading(isLoading);
 * // If API responds in < 200ms, delayedLoading will never become true
 * // If API takes > 200ms, delayedLoading becomes true after the delay
 */
export function useDelayedLoading(loading: boolean, delay: number = 200): boolean {
  const [delayedLoading, setDelayedLoading] = useState<boolean>(false);

  useEffect(() => {
    // If loading becomes true, set a timer to show the loading state after the delay
    if (loading) {
      const timer = setTimeout(() => {
        setDelayedLoading(true);
      }, delay);

      // Cleanup: clear the timer if loading becomes false before the delay
      return () => {
        clearTimeout(timer);
      };
    } else {
      // If loading becomes false, immediately hide the loading state
      setDelayedLoading(false);
    }
  }, [loading, delay]);

  return delayedLoading;
}
